import { FileImportSummary } from "@/components/benefits-discovery-v2/payroll-sync/imported-employees";
import {
  type StepBuildCeremony,
  mergeStepBuildProps,
  pageBodyBlockBuildProps,
  sectionPageIntroMotions,
} from "@/components/benefits-discovery-v2/step-sequential-build";
import { cn } from "@/lib/utils";
import { track } from "@cakewalk/frontend-api";
import { AlertCircle, ArrowRight, CheckCircle2, MapPin, Trash2, Upload } from "@cakewalk/ui/icons";
import {
  V2Accordion,
  V2AccordionContent,
  V2AccordionItem,
  V2AccordionTrigger,
  V2Avatar,
  V2AvatarFallback,
  V2Badge,
  V2Button,
  V2DateInput,
  V2EnrollmentFlowMain,
  V2FormActions,
  V2Input,
  V2QuickActionCard,
  V2Select,
  V2SelectContent,
  V2SelectItem,
  V2SelectTrigger,
  V2SelectValue,
  V2Spinner,
  V2Typography,
} from "@cakewalk/ui/v2";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import {
  SEX_OPTIONS,
  formatZip,
  makeTeamMember,
  teamMemberComplete,
  teamMemberMissingFields,
  todayInputValue,
  validDateOfBirth,
  validZip,
} from "./data";
import { RosterParseError, parseRosterFile } from "./roster-parse";
import type { HrPullSummary, Sex, TeamMember } from "./types";

/**
 * Step 3 of the existing-client POC: add the team (no payroll or 3rd-party sync
 * in this POC). One screen, both paths live at once — mirrors the main flow's
 * roster step (`ManualRoster`): a slim upload bar sits above the manual list, so
 * the client can drop a roster file OR type people in, whichever is easier.
 *
 * Per person we ask the four basics an underwriter needs to rate medical: name,
 * sex, home ZIP, and date of birth. Uploading actually parses the dropped CSV
 * (see `roster-parse.ts`) and seeds the editable cards from it; the employer
 * then confirms/corrects. Dependents are collected on the next step, not here.
 */

type UploadStatus = "idle" | "processing" | "done" | "error";

/** DOM id for a member's card, so the "needs info" list can scroll to it. */
function memberCardDomId(memberId: string): string {
  return `cc-member-card-${memberId}`;
}

/** "Dana Holt", or a stable positional fallback while the name is still blank. */
function memberDisplayName(member: TeamMember, index: number): string {
  const name = `${member.firstName} ${member.lastName}`.trim();
  return name.length > 0 ? name : `Person ${index + 1}`;
}

/**
 * Field labels as a readable sentence fragment: "a date of birth",
 * "a home ZIP and a date of birth", "a sex, a home ZIP, and a date of birth".
 *
 * Only the FIRST letter is lower-cased — these run on from "Needs ", but a
 * blanket `toLowerCase()` would render "Home ZIP" as "home zip".
 */
function formatMissingList(fields: string[]): string {
  const items = fields.map((f) => `a ${f.charAt(0).toLowerCase()}${f.slice(1)}`);
  if (items.length <= 1) return items[0] ?? "";
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
}

/** Build and download a sample roster CSV — matches the fields we collect:
 *  first/last name, sex, home ZIP, date of birth. */
function downloadSampleRoster() {
  if (typeof document === "undefined") return;
  const lines = [
    "First name,Last name,Sex,Home ZIP,Date of birth",
    "Maria,Alvarez,Female,94103,1990-05-12",
    "James,Chen,Male,94105,1985-11-03",
    "Dana,Holt,Female,94103,1993-02-21",
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  // tracking-plan: Flow 11 (company_census) > census_sample_roster_downloaded (P3)
  track("census_sample_roster_downloaded", { flow_name: "company_census", step_name: "team" });
  const link = document.createElement("a");
  link.href = url;
  link.download = "cakewalk-roster-template.csv";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function CompanyCensusTeamStep({
  members,
  rosterSource = null,
  pullSummary = null,
  playBuild = false,
  ceremony = null,
  onChange,
  onBack,
  onNext,
}: {
  members: TeamMember[];
  /** Where the roster came from — a connected HR system swaps in the review copy/banner. */
  rosterSource?: "quickbooks" | "bamboohr" | "manual" | null;
  /** What the HR pull brought in (and left out). Null for manual / CSV rosters. */
  pullSummary?: HrPullSummary | null;
  playBuild?: boolean;
  ceremony?: StepBuildCeremony | null;
  onChange: (next: TeamMember[]) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  // Both connected sources land on the same "review what we pulled" treatment;
  // only the provider's name and logo differ.
  const importedFrom =
    rosterSource === "quickbooks"
      ? { name: "QuickBooks", logoSrc: "/payroll-logos/quickbooks.png" }
      : rosterSource === "bamboohr"
        ? { name: "BambooHR", logoSrc: "/payroll-logos/bamboohr.png" }
        : null;
  // The first card opens by default; a freshly-added card opens itself.
  const [expandedId, setExpandedId] = useState<string>(() => members[0]?.id ?? "");
  // Upload bar state: a picked file is parsed (roster-parse) while a short beat
  // plays, then the real parsed people drop into the editable list below — or an
  // error is shown if the file couldn't be read.
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>("idle");
  const [fileName, setFileName] = useState<string | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  const updateMember = (id: string, patch: Partial<TeamMember>) =>
    onChange(members.map((m) => (m.id === id ? { ...m, ...patch } : m)));

  const removeMember = (id: string) => {
    // tracking-plan: Flow 11 (company_census) > census_member_removed (P2)
    track("census_member_removed", { flow_name: "company_census", step_name: "team" });
    const next = members.filter((m) => m.id !== id);
    if (expandedId === id) setExpandedId(next[0]?.id ?? "");
    onChange(next.length ? next : [makeTeamMember()]);
  };

  const addMember = () => {
    // tracking-plan: Flow 11 (company_census) > census_member_added (P2)
    track("census_member_added", { flow_name: "company_census", step_name: "team" });
    const created = makeTeamMember();
    onChange([...members, created]);
    setExpandedId(created.id);
  };

  // A picked/dropped file → parse for real while a short beat plays, then seed
  // the editable cards from the parsed rows (or surface a friendly error).
  const handleFilePicked = (file: File) => {
    setFileName(file.name);
    setParseError(null);
    setUploadStatus("processing");
    // tracking-plan: Flow 11 (company_census) > census_roster_upload_started (P2)
    track("census_roster_upload_started", { flow_name: "company_census", step_name: "team" });

    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const minBeatMs = reduce ? 0 : 820;

    void Promise.allSettled([
      parseRosterFile(file),
      new Promise<void>((resolve) => window.setTimeout(resolve, minBeatMs)),
    ]).then(([parsed]) => {
      if (parsed.status === "fulfilled") {
        const roster = parsed.value;
        onChange(roster);
        // tracking-plan: Flow 11 (company_census) > census_roster_upload_succeeded (P1)
        track("census_roster_upload_succeeded", {
          flow_name: "company_census",
          step_name: "team",
          record_count: roster.length,
        });
        setExpandedId(roster[0]?.id ?? "");
        setUploadStatus("done");
        return;
      }
      // tracking-plan: Flow 11 (company_census) > census_roster_upload_failed (P2)
      track("census_roster_upload_failed", {
        flow_name: "company_census",
        step_name: "team",
        error_reason:
          parsed.reason instanceof RosterParseError
            ? parsed.reason.message.slice(0, 80)
            : "unreadable",
      });
      const message =
        parsed.reason instanceof RosterParseError
          ? parsed.reason.message
          : "We couldn't read that file. Try a CSV, or download our sample.";
      setParseError(message);
      setUploadStatus("error");
    });
  };

  const readyCount = members.filter(teamMemberComplete).length;
  const canContinue = readyCount === members.length && readyCount > 0;
  // Who is holding the step up, in list order. Continue is disabled until this
  // is empty, so it has to be possible to SEE it — a bare "28 of 30 ready" left
  // an employer scrolling 30 collapsed cards hunting for the two.
  const incomplete = members.filter((m) => !teamMemberComplete(m));

  // Open the first person who needs attention on arrival from a payroll pull.
  // Landing on a complete card while the blocker sits 20 rows down is how the
  // step reads as broken rather than as three fields away from done.
  const focusedIncompleteRef = useRef(false);
  useEffect(() => {
    if (focusedIncompleteRef.current || !importedFrom || members.length === 0) return;
    focusedIncompleteRef.current = true;
    const first = members.find((m) => !teamMemberComplete(m));
    if (first) setExpandedId(first.id);
  }, [importedFrom, members]);

  /** Expand a person's card and bring it into view. */
  const jumpToMember = (id: string) => {
    setExpandedId(id);
    // The accordion animates open, so let it commit before we measure.
    window.requestAnimationFrame(() => {
      document
        .getElementById(memberCardDomId(id))
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  };

  const { eyebrowMotion, titleMotion, descriptionMotion } = sectionPageIntroMotions(
    playBuild,
    ceremony,
  );

  return (
    <V2EnrollmentFlowMain width="default">
      <div className="mx-auto w-full max-w-cw-v2-content-narrow pb-8">
        <div className="mb-8 flex flex-col items-center gap-2 text-center">
          <V2Typography
            variant="overline"
            {...mergeStepBuildProps(eyebrowMotion, { className: "text-cw-v2-reliant-red" })}
          >
            {importedFrom ? "Review your team" : "Team roster"}
          </V2Typography>
          <V2Typography variant="display" as="h1" {...mergeStepBuildProps(titleMotion)}>
            {importedFrom ? "Review your team" : "Who needs coverage?"}
          </V2Typography>
          <V2Typography
            variant="body-large"
            muted
            {...mergeStepBuildProps(descriptionMotion, { className: "max-w-xl" })}
          >
            {importedFrom
              ? `We pulled your team from ${importedFrom.name}. Check each person and edit anything that needs fixing.`
              : "Upload your roster, or add people one at a time — whichever is easier."}
          </V2Typography>
        </div>

        <div
          {...mergeStepBuildProps(pageBodyBlockBuildProps(playBuild, 0, ceremony), {
            className: "flex flex-col gap-6",
          })}
        >
          {/* A connected HR system → a "pulled from <provider>" status banner.
              Manual (or any other) entry → the slim drag-drop upload bar. */}
          {importedFrom ? (
            <section className="flex flex-col gap-3 rounded-[12px] border border-cw-v2-border/70 bg-transparent px-4 py-3">
              <div className="flex flex-wrap items-center gap-3">
                <img
                  src={importedFrom.logoSrc}
                  alt=""
                  aria-hidden
                  className="max-h-7 w-auto shrink-0 object-contain"
                />
                <span className="flex min-w-0 flex-1 flex-col">
                  <V2Typography variant="body" className="font-semibold">
                    Pulled from {importedFrom.name}
                  </V2Typography>
                  <V2Typography variant="meta" muted>
                    {members.length} {members.length === 1 ? "person" : "people"} imported · edit
                    any profile below
                  </V2Typography>
                </span>
              </div>

              {/* Why the roster is smaller than their payroll headcount. Without
                  this line a correct import ("120 employees, 44 current") looks
                  like it lost 76 people. */}
              {pullSummary && pullSummary.excludedFormerCount > 0 ? (
                <V2Typography variant="body-small" muted className="flex items-start gap-2">
                  <CheckCircle2
                    className="mt-0.5 h-4 w-4 shrink-0 text-cw-v2-green-600"
                    aria-hidden
                  />
                  <span>
                    We left out {pullSummary.excludedFormerCount} former{" "}
                    {pullSummary.excludedFormerCount === 1 ? "employee" : "employees"} —{" "}
                    {importedFrom.name} lists them as inactive or terminated. Need someone back? Add
                    them with &ldquo;Add another person&rdquo;.
                  </span>
                </V2Typography>
              ) : null}

              {/* A provider that withheld a field the census needs. Says so
                  plainly, so blank birth dates read as a permission boundary in
                  their HR system rather than as our import dropping data. */}
              {pullSummary?.fieldWarnings.map((warning) => (
                <V2Typography
                  key={warning}
                  variant="body-small"
                  muted
                  className="flex items-start gap-2"
                >
                  <AlertCircle
                    className="mt-0.5 h-4 w-4 shrink-0 text-cw-v2-reliant-red"
                    aria-hidden
                  />
                  <span>{warning}</span>
                </V2Typography>
              ))}
            </section>
          ) : (
            <RosterUploadBar
              status={uploadStatus}
              fileName={fileName}
              recordCount={members.length}
              errorMessage={parseError}
              onFilePicked={handleFilePicked}
            />
          )}

          {/* The step's blocker, made legible and actionable.
              `Continue` is disabled until every person is complete, and the only
              signal used to be a "28 of 30 ready" counter — which named nobody
              and led nowhere. This names each person, says what each one is
              missing, and jumps straight to their card. */}
          {incomplete.length > 0 ? (
            <section
              className="flex flex-col gap-3 rounded-[12px] border border-cw-v2-sunny-scale-200 bg-cw-v2-sunny-scale-50 p-4"
              aria-live="polite"
            >
              <div className="flex items-start gap-3">
                <AlertCircle
                  className="mt-0.5 h-4 w-4 shrink-0 text-cw-v2-sunny-scale-700"
                  aria-hidden
                />
                <div className="flex min-w-0 flex-col gap-1">
                  <V2Typography variant="body" className="font-semibold">
                    {incomplete.length === 1
                      ? "One person still needs a couple of details"
                      : `${incomplete.length} people still need a couple of details`}
                  </V2Typography>
                  <V2Typography variant="body-small" muted>
                    Underwriters rate medical on sex, home ZIP, and date of birth, so we need all
                    three for everyone before you continue.
                  </V2Typography>
                </div>
              </div>

              <ul className="flex flex-col gap-2">
                {incomplete.map((member) => {
                  const index = members.indexOf(member);
                  return (
                    <li key={member.id}>
                      <button
                        type="button"
                        onClick={() => jumpToMember(member.id)}
                        className="group flex w-full items-center gap-3 rounded-[10px] border border-cw-v2-sunny-scale-200 bg-cw-v2-surface px-3 py-2 text-left transition-colors hover:border-cw-v2-sunny-scale-400"
                        data-track="button-company-census-jump-to-incomplete"
                      >
                        <span className="min-w-0 flex-1">
                          {/* `data-sensitive`: the same masking reason as the card
                              header below — this renders a person's name as text. */}
                          <V2Typography
                            variant="body-small"
                            as="span"
                            className="block truncate font-semibold"
                            data-sensitive
                          >
                            {memberDisplayName(member, index)}
                          </V2Typography>
                          <V2Typography variant="body-small" as="span" muted className="block">
                            Needs {formatMissingList(teamMemberMissingFields(member))}
                          </V2Typography>
                        </span>
                        <ArrowRight
                          className="h-4 w-4 shrink-0 text-cw-v2-text-muted transition-transform group-hover:translate-x-0.5"
                          aria-hidden
                        />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ) : null}

          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <V2Typography variant="heading-small" as="h3">
                Your people
              </V2Typography>
              <V2Typography variant="meta" muted>
                {readyCount} of {members.length} ready
              </V2Typography>
            </div>

            <V2Accordion
              type="single"
              collapsible
              variant="separated"
              value={expandedId}
              onValueChange={setExpandedId}
              className="flex flex-col gap-3"
            >
              {members.map((member, index) => (
                <MemberCard
                  key={member.id}
                  member={member}
                  index={index}
                  canRemove={members.length > 1}
                  onUpdate={(patch) => updateMember(member.id, patch)}
                  onRemove={() => removeMember(member.id)}
                  onLooksGood={() => {
                    // tracking-plan: Flow 11 (company_census) > census_member_confirmed (P3)
                    track("census_member_confirmed", {
                      flow_name: "company_census",
                      step_name: "team",
                    });
                    const next = members[index + 1];
                    setExpandedId(next ? next.id : "");
                  }}
                />
              ))}
            </V2Accordion>

            <V2QuickActionCard
              variant="add"
              title="Add another person"
              description="Add another employee who should be covered."
              onClick={addMember}
              data-track="button-company-census-add-member"
            />
          </div>
        </div>

        <V2FormActions variant="registration" className="mt-8">
          {/* The disabled button used to be the only sign anything was wrong,
              and by the time an employer scrolls past 40 cards to reach it, the
              panel at the top is long gone. Repeat the reason at the point of
              failure, and make it a way back to the person blocking it. */}
          {incomplete.length > 0 ? (
            <V2Typography
              variant="body-small"
              muted
              className="flex flex-wrap items-center justify-center gap-1 text-center"
            >
              <span>
                {incomplete.length === 1
                  ? "One person still needs a couple of details."
                  : `${incomplete.length} people still need a couple of details.`}
              </span>
              <V2Button
                variant="link"
                size="sm"
                onClick={() => {
                  const first = incomplete[0];
                  if (first) jumpToMember(first.id);
                }}
                data-track="button-company-census-goto-first-incomplete"
              >
                Take me there
              </V2Button>
            </V2Typography>
          ) : null}
          <V2Button
            variant="primary"
            size="lg"
            fullWidth
            disabled={!canContinue}
            onClick={onNext}
            iconSuffix={<ArrowRight className="h-4 w-4" />}
            data-track="button-company-census-team-continue"
          >
            Continue to dependents
          </V2Button>
          <V2Button variant="link" onClick={onBack} data-track="button-company-census-team-back">
            Back
          </V2Button>
        </V2FormActions>
      </div>
    </V2EnrollmentFlowMain>
  );
}

// ---------------------------------------------------------------------------
// Upload bar (slim, drag-drop) — mirrors the main flow's ManualRoster upload bar
// ---------------------------------------------------------------------------

const ROSTER_ACCEPT = ".csv,text/csv";

/** Multi-step parse beat (matches the main flow's roster upload choreography). */
const UPLOAD_STEPS = ["Uploading file", "Reading columns", "Matching fields"] as const;

function RosterUploadBar({
  status,
  fileName,
  recordCount,
  errorMessage,
  onFilePicked,
}: {
  status: UploadStatus;
  fileName: string | null;
  recordCount: number;
  errorMessage: string | null;
  onFilePicked: (file: File) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);

  const handleFile = (file: File | undefined) => {
    if (!file) return;
    onFilePicked(file);
  };

  if (status === "processing") {
    return <UploadProcessing fileName={fileName ?? "your file"} />;
  }

  if (status === "done") {
    return (
      <>
        <FileImportSummary
          fileName={fileName ?? "your file"}
          recordCount={recordCount}
          onUploadAnother={() => inputRef.current?.click()}
        />
        <input
          ref={inputRef}
          type="file"
          accept={ROSTER_ACCEPT}
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0] ?? undefined)}
        />
      </>
    );
  }

  if (status === "error") {
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-[12px] border border-cw-v2-red-200 bg-cw-v2-red-50 p-4">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-cw-v2-reliant-red/10 text-cw-v2-reliant-red">
          <AlertCircle className="h-4 w-4" />
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <V2Typography variant="body" className="font-semibold">
            We couldn&rsquo;t import {fileName ?? "that file"}
          </V2Typography>
          <V2Typography variant="meta" muted>
            {errorMessage ?? "Try a CSV, or download our sample."}{" "}
            <V2Button
              variant="link"
              size="sm"
              onClick={() => downloadSampleRoster()}
              data-track="link-company-census-download-sample-roster"
            >
              download a sample
            </V2Button>
          </V2Typography>
        </span>
        <V2Button
          variant="secondary"
          size="md"
          onClick={() => inputRef.current?.click()}
          data-track="button-company-census-roster-upload-retry"
        >
          Try another file
        </V2Button>
        <input
          ref={inputRef}
          type="file"
          accept={ROSTER_ACCEPT}
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0] ?? undefined)}
        />
      </div>
    );
  }

  return (
    // A div (not a button) so the nested "download a sample" button is valid DOM;
    // keyboard + role keep it operable as the upload affordance.
    // biome-ignore lint/a11y/useSemanticElements: must be a div — it contains a nested button (invalid inside <button>)
    <div
      role="button"
      tabIndex={0}
      onClick={() => inputRef.current?.click()}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          inputRef.current?.click();
        }
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setDragActive(true);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        setDragActive(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setDragActive(false);
        handleFile(e.dataTransfer.files?.[0]);
      }}
      data-track="button-company-census-roster-upload"
      className={cn(
        "flex w-full cursor-pointer flex-wrap items-center gap-x-3 gap-y-1 rounded-[12px] border border-dashed p-4 text-left transition-colors",
        dragActive
          ? "border-cw-v2-accent bg-cw-v2-accent/5"
          : "border-cw-v2-border hover:border-cw-v2-accent/60 hover:bg-cw-v2-sidewalk/40",
      )}
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-cw-v2-accent/10 text-cw-v2-accent">
        <Upload className="h-4 w-4" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <V2Typography variant="body" className="font-semibold">
          Have a roster file? Drop it here or browse
        </V2Typography>
        <V2Typography variant="meta" muted>
          CSV file · up to 10MB ·{" "}
          <V2Button
            variant="link"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              downloadSampleRoster();
            }}
            data-track="link-company-census-download-sample-roster"
          >
            download a sample
          </V2Button>
        </V2Typography>
      </span>
      <span className="shrink-0 rounded-cw-v2-md border border-cw-v2-border px-3 py-1.5 text-sm font-semibold text-cw-v2-ink">
        Browse files
      </span>
      <input
        ref={inputRef}
        type="file"
        accept={ROSTER_ACCEPT}
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0] ?? undefined)}
      />
    </div>
  );
}

/**
 * Animated upload/parse beat shown while the dropped file is being parsed —
 * mirrors the main flow's `UploadProcessing`: a spinner + stepped status label
 * ("Uploading file" → "Reading columns" → "Matching fields") over an animated
 * progress bar. Purely visual; the parent flips to done/error when parsing
 * resolves.
 */
function UploadProcessing({ fileName }: { fileName: string }) {
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    const stepMs = 520;
    const timers = UPLOAD_STEPS.map((_, i) => window.setTimeout(() => setStepIndex(i), i * stepMs));
    return () => {
      timers.forEach(window.clearTimeout);
    };
  }, []);

  const progress = ((stepIndex + 1) / UPLOAD_STEPS.length) * 100;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
      className="rounded-[12px] border border-cw-v2-border bg-cw-v2-surface p-5"
    >
      <div className="flex items-center gap-3">
        <V2Spinner size="sm" />
        <div className="flex min-w-0 flex-1 flex-col">
          <V2Typography variant="body" className="truncate font-semibold">
            {fileName}
          </V2Typography>
          <AnimatePresence mode="wait">
            <motion.div
              key={stepIndex}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.18 }}
            >
              <V2Typography variant="meta" muted>
                {UPLOAD_STEPS[stepIndex]}…
              </V2Typography>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-cw-v2-border">
        <motion.div
          className="h-full rounded-full bg-cw-v2-accent"
          initial={{ width: "8%" }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        />
      </div>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// Member card
// ---------------------------------------------------------------------------

function MemberCard({
  member,
  index,
  canRemove,
  onUpdate,
  onRemove,
  onLooksGood,
}: {
  member: TeamMember;
  index: number;
  canRemove: boolean;
  onUpdate: (patch: Partial<TeamMember>) => void;
  onRemove: () => void;
  onLooksGood: () => void;
}) {
  const complete = teamMemberComplete(member);
  const missing = teamMemberMissingFields(member);
  const displayName = memberDisplayName(member, index);
  const initials =
    `${member.firstName.trim().charAt(0)}${member.lastName.trim().charAt(0)}`.toUpperCase() ||
    String(index + 1);
  // An incomplete row says what it needs; a complete one shows what we hold.
  // "Sex missing · ZIP missing" named only two of the five fields, so a card
  // blocked on a birth date looked identical to one that was fine.
  const summary = complete
    ? [member.sex === "female" ? "Female" : "Male", `ZIP ${member.zip}`].join(" · ")
    : `Needs ${formatMissingList(missing)}`;

  const zipError =
    member.zip.length > 0 && !validZip(member.zip) ? "ZIP must be 5 or 9 digits." : undefined;
  const dobError =
    member.dateOfBirth.length > 0 && !validDateOfBirth(member.dateOfBirth)
      ? "Enter a valid date of birth."
      : undefined;

  return (
    <V2AccordionItem
      value={member.id}
      id={memberCardDomId(member.id)}
      className="shadow-cw-v2-card scroll-mt-24"
    >
      <V2AccordionTrigger data-track={`button-company-census-member-toggle-${member.id}`}>
        {/*
          `data-sensitive` on all three: this header renders member data as TEXT, and
          `maskAllInputs` only masks input *values*. Without these the session replay records
          every team member's full name, initials, sex and ZIP in plain sight — while
          `App.tsx` asserts "DOB / ZIP / SSN / comp / addresses never enter a replay".
          The inputs below are already covered by the global mask; this row was not.
        */}
        <span className="flex w-full items-center gap-3">
          <V2Avatar size="md">
            <V2AvatarFallback data-sensitive>{initials}</V2AvatarFallback>
          </V2Avatar>
          <span className="min-w-0 flex-1 text-left">
            <V2Typography
              variant="body"
              as="span"
              className="block truncate font-semibold"
              data-sensitive
            >
              {displayName}
            </V2Typography>
            <V2Typography
              variant="body"
              as="span"
              muted
              className="line-clamp-2 block"
              data-sensitive
            >
              {summary}
            </V2Typography>
          </span>
          {/* "Open" told the employer a state, not a task — one customer read it
              as an error they couldn't clear. "Needs info" names the action. */}
          <V2Badge
            variant={complete ? "success" : "warning"}
            size="xs"
            label={complete ? "Complete" : "Needs info"}
          />
        </span>
      </V2AccordionTrigger>

      <V2AccordionContent>
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <V2Input
              label="First name"
              size="md"
              value={member.firstName}
              onChange={(e) => onUpdate({ firstName: e.target.value })}
              autoComplete="off"
            />
            <V2Input
              label="Last name"
              size="md"
              value={member.lastName}
              onChange={(e) => onUpdate({ lastName: e.target.value })}
              autoComplete="off"
            />
            <SexSelect id={member.id} value={member.sex} onChange={(sex) => onUpdate({ sex })} />
            <V2Input
              label="Home ZIP code"
              size="md"
              value={member.zip}
              onChange={(e) => onUpdate({ zip: formatZip(e.target.value) })}
              inputMode="numeric"
              maxLength={10}
              placeholder="94103"
              name="postal-code"
              autoComplete="off"
              error={zipError}
              prefix={<MapPin className="h-4 w-4" aria-hidden />}
              data-sensitive
            />
            <V2DateInput
              label="Date of birth"
              size="md"
              value={member.dateOfBirth}
              min="1900-01-01"
              max={todayInputValue()}
              onValueChange={(value) => onUpdate({ dateOfBirth: value })}
              error={dobError}
              data-sensitive
            />
          </div>

          {complete ? (
            <div className="bd-v2-confirmed-data flex items-center gap-2 rounded-[12px] border p-3">
              <CheckCircle2 className="bd-v2-confirmed-data__icon h-4 w-4 shrink-0" />
              <V2Typography variant="body">This person&rsquo;s details are ready.</V2Typography>
            </div>
          ) : null}

          {complete || canRemove ? (
            <div className="flex flex-wrap justify-end gap-2">
              {canRemove && (
                <V2Button
                  variant="ghost"
                  size="md"
                  iconPrefix={<Trash2 className="h-4 w-4" />}
                  onClick={onRemove}
                  data-track={`button-company-census-remove-member-${member.id}`}
                >
                  Remove
                </V2Button>
              )}
              {complete && (
                <V2Button
                  variant="primary"
                  size="md"
                  onClick={onLooksGood}
                  data-track={`button-company-census-member-looks-good-${member.id}`}
                >
                  Looks good
                </V2Button>
              )}
            </div>
          ) : null}
        </div>
      </V2AccordionContent>
    </V2AccordionItem>
  );
}

function SexSelect({
  id,
  value,
  onChange,
}: {
  id: string;
  value: Sex | "";
  onChange: (sex: Sex) => void;
}) {
  return (
    <div className="cw-v2-input-field">
      <V2Select value={value || undefined} onValueChange={(next) => onChange(next as Sex)}>
        <V2SelectTrigger id={`${id}-sex`} label="Sex" size="md" aria-label="Sex">
          <V2SelectValue placeholder="Select" />
        </V2SelectTrigger>
        <V2SelectContent>
          {SEX_OPTIONS.map((option) => (
            <V2SelectItem key={option.value} value={option.value}>
              {option.label}
            </V2SelectItem>
          ))}
        </V2SelectContent>
      </V2Select>
    </div>
  );
}
